/* 2026 header: drawer close paths, and the mobile search relocation.

   THE BLOCKER THIS FIXES
   ----------------------
   Measured on 390px before this existed: the drawer opened full-width at
   z-index 5 covering the header, its only close button carried
   `small-hide medium-hide` and computed to display:none, and no overlay
   element existed. elementFromPoint at the hamburger's own coordinates
   returned the drawer's search input. There was no tap target that closed
   it — browser-back was the only way out.

   Four independent close paths now, deliberately redundant:
     1. the hamburger glyph, kept above the drawer and morphed to an X (CSS)
     2. the in-drawer X, un-hidden on mobile (CSS)
     3. tapping the backdrop — Dawn's own `summary::before`, which already
        toggles the <details>. An earlier version of this file created a
        SECOND backdrop because a DOM query for an overlay element returned
        nothing; Dawn's is a pseudo-element and no query can ever find one.
        That duplicate is gone.
     4. Escape — MenuDrawer.onKeyUp in global.js, which closes and restores
        focus through its own trap. This file deliberately does NOT add a
        second Escape handler; two racing on one key is worse than one.

   Paths 1-2 work with JS disabled.

   The drawer is Dawn's <header-drawer> with a <details>/<summary> inside.
   Open state is the `open` attribute on the <details>, so closing means
   removing it — not calling a method on a custom element that may not have
   upgraded.
*/
(() => {
  const DESKTOP = '(min-width: 990px)';

  const parts = () => {
    const root = document.querySelector('.rb-header--2026');
    if (!root) return null;
    const drawerEl = root.querySelector('header-drawer');
    if (!drawerEl) return null;
    return {
      root,
      drawerEl,
      details: drawerEl.querySelector('details'),
      summary: drawerEl.querySelector('summary'),
      panel: drawerEl.querySelector('.rb-drawer__panel'),
    };
  };

  /* Close through Dawn's own method, with a REAL event object.

     closeMenuDrawer(event) returns early when event === undefined, so a bare
     call silently does nothing. Removing the `open` attribute directly does
     work, but skips the body scroll unlock, removeTrapFocus and the
     aria-expanded reset. Passing a fabricated event satisfies the guard and
     runs the full cleanup, including focus restoration through Dawn's trap —
     which is why this file no longer has an Escape handler of its own:
     MenuDrawer.onKeyUp already does it, and does it better. */
  const close = (p) => {
    if (!p || !p.details || !p.details.hasAttribute('open')) return;
    if (p.drawerEl && typeof p.drawerEl.closeMenuDrawer === 'function') {
      p.drawerEl.closeMenuDrawer(new CustomEvent('rb-close'), p.summary);
      return;
    }
    /* Fallback only if the custom element never upgraded. */
    p.details.removeAttribute('open');
    if (p.summary) p.summary.focus();
  };

  const activate = (el, fn) => {
    el.addEventListener('click', fn);
    el.addEventListener('keydown', (event) => {
      /* role="button" gets no implicit keyboard activation — Enter and Space
         have to be wired by hand. Space is prevented so it does not scroll. */
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      event.preventDefault();
      fn(event);
    });
  };

  const mountDrawer = () => {
    const p = parts();
    if (!p || !p.details || p.drawerEl.dataset.rbClose) return;
    p.drawerEl.dataset.rbClose = 'true';

    /* Close x and scrim. Both are <span>, never <button>: MenuDrawer's
       bindEvents() binds every <button> inside <header-drawer> to
       onCloseButtonClick, which calls closeSubmenu and moves focus. */
    /* No [data-rb-drawer-close] any more — the morphing <summary> is the close
       control, and clicking a <summary> closes its <details> natively. */
    const scrim = p.drawerEl.querySelector('.rb-drawer__scrim');
    if (scrim) scrim.addEventListener('click', () => close(p));

    /* Submenu disclosure — ours, not Dawn's nested <details>. Keeping the
       main toggle the only <summary> means bindEvents() cannot bind these,
       and nothing waits on a transitionend that may never fire. */
    p.drawerEl.querySelectorAll('[data-rb-submenu-toggle]').forEach((toggle) => {
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!panel) return;
      activate(toggle, () => {
        const open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!open));
        panel.hidden = open;
      });
    });

    /* Escape. MenuDrawer.onKeyUp exists but is bound to `keyup` AND requires
       event.target.closest('details[open]'), so it only fires when focus is
       already inside the drawer — measured not closing. This handles the rest
       and defers to Dawn's cleanup via close(). */
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const q = parts();
      if (!q || !q.details || !q.details.hasAttribute('open')) return;
      close(q);
    });

    /* --header-bottom-position: Dawn writes it once, in openMenuDrawer. That
       leaves two gaps this closes.

       1. It does not exist before the first open, so the drawer's `top` would
          fall back to 0 on that first paint. Seeded here at mount.
       2. It is never refreshed, so rotating the device or resizing while the
          drawer is open leaves the offset measured against the old header
          height. Recomputed on both. */
    const syncHeaderBottom = () => {
      const header = document.querySelector('.section-header') || p.root.closest('.header-wrapper');
      if (!header) return;
      const bottom = Math.round(header.getBoundingClientRect().bottom);
      if (bottom > 0) document.documentElement.style.setProperty('--header-bottom-position', bottom + 'px');
    };
    syncHeaderBottom();
    window.addEventListener('resize', syncHeaderBottom);
    window.addEventListener('orientationchange', () => setTimeout(syncHeaderBottom, 120));

    /* Search icon opens the DRAWER with its field focused, rather than
       navigating — one search implementation instead of two.

       It opens by clicking the summary, i.e. exactly the path the hamburger
       takes, so Dawn's openMenuDrawer still runs: menu-opening, aria-expanded,
       trapFocus and the body scroll lock. Focusing the field afterwards is
       safe from onFocusOut because the field lives inside the same <details>;
       onFocusOut only closes when activeElement lands OUTSIDE it.

       The href stays a real search URL, so it still works without JS and for
       anyone opening it in a new tab. */
    document.querySelectorAll('[data-rb-search-open]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        const q = parts();
        const field = q && q.drawerEl.querySelector('.rb-drawer__search-input');
        if (!q || !q.summary || !field) return;   // fall through to the href
        event.preventDefault();
        if (!q.details.hasAttribute('open')) q.summary.click();
        /* after the open transition starts, so focus is not stolen mid-slide */
        setTimeout(() => field.focus(), 60);
      });
    });

    /* Any nav link closes the drawer — an in-page anchor would otherwise
       leave it covering the thing it just scrolled to. */
    p.drawerEl.addEventListener('click', (event) => {
      if (event.target.closest('a[href]')) close(p);
    });
  };

  /* The design puts search in the LEFT cluster on mobile and the RIGHT
     cluster on desktop. Those are different grid cells, so CSS alone cannot
     move it. The node is relocated rather than duplicated: a second
     <details-modal> would mean a second search input carrying the same id,
     and two dialogs announcing themselves to assistive tech.

     Same pattern the announcement bar already uses to relocate itself into
     <sticky-header>. */
  const placeSearch = () => {
    const root = document.querySelector('.rb-header--2026');
    if (!root) return;
    const search = root.querySelector('.header__search');
    const left = root.querySelector('.rb-header__left');
    const icons = root.querySelector('.header__icons');
    if (!search || !left || !icons) return;

    const wantsLeft = !window.matchMedia(DESKTOP).matches;
    if (wantsLeft && search.parentElement !== left) {
      left.appendChild(search);
    } else if (!wantsLeft && search.parentElement !== icons) {
      icons.insertBefore(search, icons.firstChild);
    }
  };

  const mount = () => {
    mountDrawer();
    placeSearch();
  };

  document.addEventListener('DOMContentLoaded', mount);
  window.matchMedia(DESKTOP).addEventListener('change', placeSearch);
  document.addEventListener('shopify:section:load', mount);
})();
